import React, { useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Button, Grid } from '@mui/material'
import { translate } from '../i18n'
import { UserListParams } from '../model'
import {
    AnyAutocomplete,
    AutocompleteOption,
    AnyInput,
    useValidatedForm,
} from '../anyform'
import { UsersFilterProps } from './UsersFilter.types'

function UsersFilter({ roles }: UsersFilterProps) {
    const history = useHistory()
    const query = new URLSearchParams(useLocation().search)
    const search = query.get('search')
    const roleId = query.get('roleId')
    const { register, bind } = useValidatedForm<UserListParams>()

    const roleOptions = useMemo<AutocompleteOption[]>(() => {
        return roles.map((el) => ({
            value: el.id,
            label: translate(`users.roles.${el.id}`) as string,
        }))
    }, [roles])

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const request = bind()
        query.set('search', request.search ?? '')
        query.set('roleId', request.roleId ?? '')
        query.set('page', '1')

        history.push(`${window.location.pathname}?${query.toString()}`)
    }

    return (
        <form autoComplete="off" noValidate onSubmit={onSubmit}>
            <Grid
                container
                alignItems="center"
                justifyContent="flex-end"
                spacing={1}
            >
                <Grid item xs={12} md>
                    <AnyInput
                        type="text"
                        name="search"
                        label={translate('users.filter') as string}
                        initialValue={search}
                        register={register}
                    />
                </Grid>
                <Grid item xs md={3}>
                    <AnyAutocomplete
                        name="roleId"
                        label={translate('users.roles.title') as string}
                        initialValue={roleId ?? undefined}
                        options={roleOptions}
                        register={register}
                        prefillWhenSingle={false}
                    />
                </Grid>
                <Grid item xs="auto">
                    <Button type="submit" color="secondary" size="medium">
                        {translate('buttons.search')}
                    </Button>
                </Grid>
            </Grid>
        </form>
    )
}

const useUsersParams = (query: URLSearchParams) => {
    const search = query.get('search')
    const roleId = query.get('roleId')
    const params = useMemo(() => {
        return {
            search: search || '',
            roleId: roleId || '',
        } as UserListParams
    }, [search, roleId])

    return params
}

export default UsersFilter

export { useUsersParams }
